import React, { useState, useRef, useEffect } from 'react';
import { API_URL } from '../../config';
import { useNavigate } from 'react-router-dom';

const ChatBot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { 
      text: "Hello! I'm your Pump Gym assistant. How can I help you today? Ask me about our features, workout plans, diet plans, or real-time tracking.", 
      sender: 'bot' 
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isError, setIsError] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [typewriterIndex, setTypewriterIndex] = useState(0);
  const [isTypewriting, setIsTypewriting] = useState(false);
  const [currentTypewriterMessage, setCurrentTypewriterMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  
  // Load chat history from localStorage when component mounts
  useEffect(() => {
    const savedMessages = localStorage.getItem('pump_gym_chat_history');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        if (Array.isArray(parsedMessages) && parsedMessages.length > 0) {
          setMessages(parsedMessages);
        }
      } catch (error) {
        console.error('Error parsing saved chat history:', error);
      }
    }
  }, []);
  
  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('pump_gym_chat_history', JSON.stringify(messages));
    }
  }, [messages]);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typewriterText]);

  // Check login status on component mount
  useEffect(() => {
    const checkLoginStatus = () => {
      const userData = localStorage.getItem('user');
      setIsLoggedIn(!!userData);
    };
    
    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  // Typewriter effect
  useEffect(() => {
    if (isTypewriting && currentTypewriterMessage && typewriterIndex < currentTypewriterMessage.length) {
      const timer = setTimeout(() => {
        setTypewriterText(prevText => prevText + currentTypewriterMessage[typewriterIndex]);
        setTypewriterIndex(prevIndex => prevIndex + 1);
      }, 15); // Speed of typewriter
      
      return () => clearTimeout(timer);
    } 
    else if (isTypewriting && typewriterIndex >= currentTypewriterMessage.length) {
      // Finished typewriting
      setIsTypewriting(false);
      setIsTyping(false); // Also ensure typing indicator is hidden
      setTypewriterIndex(0);
      setCurrentTypewriterMessage(null);
      
      // Replace the last message with the completed typewritten text and remove isAnimating flag
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          ...newMessages[newMessages.length - 1],
          text: typewriterText,
          isAnimating: false // Remove isAnimating flag when animation completes
        };
        return newMessages;
      });
    }
  }, [isTypewriting, typewriterIndex, currentTypewriterMessage]);

  // Basic responses for common greetings and simple questions
  const basicResponses = {
    greetings: [
      "Hello! How can I help you with Pump Gym today?",
      "Hi there! I'm your Pump Gym assistant. What would you like to know?",
      "Welcome to Pump Gym! I'm here to help with all your fitness questions."
    ],
    howAreYou: [
      "I'm doing great, thanks for asking! How can I help you with Pump Gym today?",
      "I'm well, thanks! Ready to assist you with any questions about our fitness platform.",
      "I'm functioning perfectly! What would you like to know about Pump Gym?"
    ],
    thanks: [
      "You're welcome! Is there anything else I can help you with?",
      "Happy to help! Let me know if you have any other questions.",
      "Anytime! Feel free to ask if you need anything else."
    ],
    bye: [
      "Goodbye! Feel free to chat again when you need assistance.",
      "Take care! I'm here whenever you need help with Pump Gym.",
      "See you later! Come back anytime you have questions."
    ],
    // Subscription-related responses
    subscription: [
      "Pump Gym offers several subscription plans available in both USD and INR to match your fitness needs. We have Free, Basic, Premium (Most popular), and Pro (Best value) plans. Each plan includes different numbers of plan generations and features. Would you like me to explain a specific plan in detail?"
    ],
    free_plan: [
      "Our Free plan costs $0 (₹0) and gives you access to 10 plan generations with limited access to our exercise tracking features. It includes a 7-day free trial and is a great way to try out Pump Gym before committing to a paid plan."
    ],
    basic_plan: [
      "The Basic plan costs $25/month (discounted from $34.65) or ₹1299/month (discounted from ₹1799) for 1 month duration. It includes 40 plan generations and all exercise tracking features. Save 27.85% with this plan!"
    ],
    premium_plan: [
      "Our Premium plan is our most popular option! USD: $20/month for 3 months (discounted from $35.98) or INR: ₹999/month for 6 months (discounted from ₹1799). It includes 200 plan generations, all exercise tracking features, and personalized mentoring. Save 44.47%!"
    ],
    pro_plan: [
      "The Pro plan offers the best value! USD: $16.67/month for 12 months (discounted from $37.51) or INR: ₹799/month for 12 months (discounted from ₹1797). It includes 500 plan generations, all exercise tracking features, and personalized mentoring. Save 55.59%!"
    ],
    view_plans: [
      "You can view and purchase our subscription plans by visiting the Subscription page. Would you like me to take you there now?"
    ],
    clear_history: [
      "I've cleared our conversation history. What would you like to talk about now?"
    ],
    // Currency conversion responses
    inr_conversion: [
      "Here are our current plans in both currencies:\n\n• Free Plan: $0 / ₹0 (7-day trial)\n• Basic Plan: $25/month / ₹1299/month (Save 27.85%)\n• Premium Plan: $20/month (3 months) / ₹999/month (6 months) - Most popular (Save 44.47%)\n• Pro Plan: $16.67/month (12 months) / ₹799/month (12 months) - Best value (Save 55.59%)\n\nWould you like specific details about any of these plans?"
    ]
  };

  // Fallback responses for when there's an error
  const fallbackResponses = {
    "error": "I'm having trouble connecting to our servers right now. Please try again later or browse our app to learn about our features.",
    "offline": [
      "I'm working in offline mode right now. Pump Gym offers three main features: real-time exercise tracking, personalized diet plans, and custom workout routines.",
      "Without a connection to our servers, I can only provide basic information. Pump Gym helps you track workouts, create meal plans, and design exercise routines.",
      "I can't access my full knowledge right now. Pump Gym is your all-in-one fitness companion for tracking workouts, planning meals, and following personalized exercise programs."
    ]
  };

  // Enhanced check for plan-specific questions
  const getPlanTypeFromMessage = (message) => {
    const lowerMsg = message.toLowerCase();
    
    if (/\bfree(\s+plan)?\b/.test(lowerMsg)) return 'free_plan';
    if (/\bbasic(\s+plan)?\b/.test(lowerMsg)) return 'basic_plan';
    if (/\bpremium(\s+plan)?\b/.test(lowerMsg)) return 'premium_plan';
    if (/\bpro(\s+plan)?\b/.test(lowerMsg)) return 'pro_plan';
    
    return null;
  };

  // Check if a message is a basic question that can be answered locally
  const isBasicQuestion = (message) => {
    const normalizedMessage = message.toLowerCase().trim();
    
    // Check for greetings
    if (/^(hi|hello|hey|greetings|yo|hola)( there)?(!)?$/.test(normalizedMessage)) {
      return { type: 'greetings' };
    }
    
    // Check for "how are you" type questions
    if (/^(how are you|how('s| is) it going|what's up|how('s| are) things|how do you do)(\?)?$/.test(normalizedMessage)) {
      return { type: 'howAreYou' };
    }
    
    // Check for thanks and general appreciation
    if (/^(thanks|thank you|thx|appreciate it|ty|thank)(!)?$/.test(normalizedMessage) || 
        /^(good|great|awesome|excellent|amazing|nice|wonderful|perfect|cool|fantastic|terrific|love it|brilliant)(!)?$/.test(normalizedMessage) ||
        /^(appreciate|well done|good job|nicely done|good work|that works|looks good|sounds good|that's helpful|helpful)(!)?$/.test(normalizedMessage)) {
      return { type: 'thanks' };
    }
    
    // Check for goodbyes
    if (/^(bye|goodbye|see you|later|cya|take care)(!)?$/.test(normalizedMessage)) {
      return { type: 'bye' };
    }
    
    // Check for specific plan questions first (higher priority)
    const planType = getPlanTypeFromMessage(normalizedMessage);
    if (planType) {
      return { type: planType };
    }
    
    // Check for currency conversion questions
    if (/\b(inr|indian rupee|rupees|₹|rs\.?|convert to inr|in india|cost in india|price in india|indian price)\b/i.test(normalizedMessage) && 
        /\b(cost|price|subscription|plan|payment)\b/i.test(normalizedMessage)) {
      return { type: 'inr_conversion' };
    }
    
    // Check for general subscription related questions (but exclude "how to" questions)
    if (/\b(subscription|plan|pricing|price|cost|payment|subscribe)\b/i.test(normalizedMessage) && 
        !/\b(how to|how do|how can|create|generate|make|build|start|track|use)\b/i.test(normalizedMessage)) {
      return { type: 'subscription' };
    }
    
    // Check if user wants to view or purchase plans
    if (/\b(view|see|purchase|buy|get|upgrade to) (plans?|subscriptions?)\b/i.test(normalizedMessage)) {
      return { type: 'view_plans', action: 'navigate' };
    }
    
    // Check if user wants to clear chat history
    if (/\b(clear|delete|remove|reset) (chat|conversation|history)\b/i.test(normalizedMessage)) {
      return { type: 'clear_history', action: 'clear' };
    }
    
    // Not a basic question
    return false;
  };

  // Clear chat history
  const clearChatHistory = async () => {
    try {
      // Clear backend conversation history
      if (isLoggedIn) {
        // For logged-in users, call the authenticated endpoint
        const response = await fetch(`${API_URL}/clear-chat-history`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for authentication
        });
        
        if (!response.ok) {
          console.error('Failed to clear backend chat history');
        }
      } else {
        // For non-logged-in users, call the public endpoint
        const response = await fetch(`${API_URL}/clear-public-chat-history`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          console.error('Failed to clear backend chat history');
        }
      }
    } catch (error) {
      console.error('Error clearing backend chat history:', error);
    }
    
    // Clear frontend state and localStorage
    const welcomeMessage = { 
      text: "Hello! I'm your Pump Gym assistant. How can I help you today? Ask me about our features, workout plans, diet plans, or real-time tracking.", 
      sender: 'bot' 
    };
    setMessages([welcomeMessage]);
    localStorage.removeItem('pump_gym_chat_history');
  };

  // Convert markdown to HTML
  const formatMarkdown = (text) => {
    if (!text) return '';
    
    // Check for the specific features list introduction pattern
    if (text.includes("features in the Pump Gym fitness application")) {
      // Apply specific formatting for the intro text
      let formatted = text.replace(
        /I can help you with the following features[^:]*/,
        '<div class="mb-3">I can help you with the following features in the Pump Gym fitness application:</div>'
      );
      
      // Format each feature with proper styling
      formatted = formatted.replace(
        /(\d+)\.\s+([\w\s-]+):\s+([\w\s,.]+)/g, 
        '<div class="flex mb-2.5">' +
          '<div class="text-primary font-bold mr-2" style="color:#14b8a6">$1.</div>' +
          '<div>' +
            '<span class="font-bold">$2</span>' +
            '<div class="ml-0">$3</div>' +
          '</div>' +
        '</div>'
      );
      
      // Add final note if present
      formatted = formatted.replace(
        /Feel free to ask[^!]*!/,
        '<div class="mt-2">Feel free to ask me more specific questions about any of these features!</div>'
      );
      
      return formatted;
    }
    
    // Format bold text
    let formattedText = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Format numbered feature lists with title and bullet points
    formattedText = formattedText.replace(/(\d+)\.\s*([^:]+?):\s*\n((?:- .+\n?)+)/gm, (match, number, title, bullets) => {
      // Process each bullet point in the list
      const bulletPoints = bullets.split('\n')
        .filter(line => line.trim().startsWith('-'))
        .map(line => {
          const bulletText = line.replace(/^-\s+/, '').trim();
          return `<li class="ml-6 relative pl-4 mb-1 before:content-['•'] before:absolute before:left-0 before:text-primary">${bulletText}</li>`;
        })
        .join('');
        
      return `<div class="mt-4 mb-3 first:mt-0">
        <div class="flex items-baseline">
          <span class="font-bold mr-2 text-primary">${number}.</span>
          <strong class="font-bold">${title.trim()}</strong>
        </div>
        <ul class="mt-0 mb-2">${bulletPoints}</ul>
      </div>`;
    });
    
    // Format numbered lists with proper styling for title and description
    formattedText = formattedText.replace(/^(\d+)\.\s+(.+?):(.+)$/gm, (match, number, title, description) => {
      return `<div class="mt-4 mb-3 first:mt-0">
        <div class="flex items-baseline">
          <span class="font-bold mr-2 text-primary">${number}.</span>
          <strong class="font-bold">${title.trim()}</strong>
          <span>${description.trim()}</span>
        </div>
      </div>`;
    });
    
    // Format regular numbered lists
    formattedText = formattedText.replace(/^(\d+)\.\s+(.+)$/gm, '<div class="flex items-baseline mt-4 mb-3 first:mt-0"><span class="font-bold mr-2 text-primary">$1.</span> <span>$2</span></div>');
    
    // Format bullet points
    formattedText = formattedText.replace(/^\*\s+(.+)$/gm, '<div class="flex items-baseline mt-1 mb-2"><span class="font-bold mr-2 text-primary">•</span> <span>$1</span></div>');
    
    // Handle paragraph breaks
    formattedText = formattedText.replace(/\n\n/g, '</p><p>');
    
    return formattedText;
  };

  // Start typewriter animation for new bot messages
  const startTypewriter = (text) => {
    setTypewriterText('');
    setTypewriterIndex(0);
    setCurrentTypewriterMessage(text);
    setIsTypewriting(true);
    
    // Add a temporary empty message that will be filled by the typewriter
    setMessages(prev => [...prev, { text: '', sender: 'bot', isAnimating: true }]);
  };

  // Handle a basic question with a predefined response
  const handleBasicQuestion = (type, action) => {
    const responses = basicResponses[type];
    const randomResponse = responses[Math.floor(Math.random() * responses.length)];
    
    // Simulate typing delay
    setTimeout(() => {
      startTypewriter(randomResponse);
      
      // Handle any actions after response is complete
      if (action === 'navigate' && type === 'view_plans') {
        setTimeout(() => {
          setMessages(prev => [...prev, { 
            text: "I'll redirect you to our subscription page in a moment...", 
            sender: 'bot' 
          }]);
          
          // Navigate to subscription page after a short delay
          setTimeout(() => {
            navigate('/subscription');
            if (onClose) onClose();
          }, 1500);
        }, 1000 + randomResponse.length * 15); // Wait for typewriter to finish
      } else if (action === 'clear' && type === 'clear_history') {
        setTimeout(async () => {
          await clearChatHistory();
        }, randomResponse.length * 15); // Wait for typewriter to finish
      }
    }, 500);
  };

  // Check if a message is specifically about pricing or details of a plan
  const isPlanDetailQuestion = (message) => {
    const lowerMsg = message.toLowerCase();
    const planTypes = ['free', 'basic', 'premium', 'pro'];
    const detailKeywords = ['price', 'cost', 'how much', 'features', 'include', 'offer', 'detail', 'about', 'what is'];
    
    for (const plan of planTypes) {
      if (lowerMsg.includes(plan)) {
        for (const keyword of detailKeywords) {
          if (lowerMsg.includes(keyword)) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  // Process the user's message and generate a response
  const processMessage = async (userMessage) => {
    setIsTyping(true);
    
    // Check if it's a plan detail question or currency conversion question for higher priority handling
    if (isPlanDetailQuestion(userMessage) || userMessage.toLowerCase().includes('rupee') || userMessage.toLowerCase().includes('inr')) {
      const basicCheck = isBasicQuestion(userMessage);
      if (basicCheck) {
        handleBasicQuestion(basicCheck.type, basicCheck.action);
        return;
      }
    }
    
    // Check if it's any other basic question
    const basicCheck = isBasicQuestion(userMessage);
    if (basicCheck) {
      handleBasicQuestion(basicCheck.type, basicCheck.action);
      return;
    }
    
    try {
      let response;
      
      // Filter out the first welcome message when sending history to the backend
      // This helps keep the context focused on the actual conversation
      // Always send the current history (even if empty) to ensure backend sync
      const historyToSend = messages.length > 1 ? 
        messages.slice(1) : [];
      
      // Use different endpoints based on login status
      if (isLoggedIn) {
        // For logged-in users, use the authenticated endpoint
        // console.log("Using authenticated endpoint");
        try {
          // Make the request with credentials included to send cookies
          const apiResponse = await fetch(`${API_URL}/chatbot`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include', // This is crucial for cookie-based auth
            body: JSON.stringify({ 
              message: userMessage,
              conversation_history: historyToSend
            }),
          });
          
          if (!apiResponse.ok) {
            console.error(`API responded with status: ${apiResponse.status}`);
            const errorText = await apiResponse.text();
            console.error(`Error details: ${errorText}`);
            throw new Error(`API responded with status: ${apiResponse.status}`);
          }
          
          response = await apiResponse.json();
        } catch (authError) {
          console.error("Authentication error:", authError);
          
          // If authentication fails, try the public endpoint as fallback
          // console.log("Falling back to public endpoint due to auth error");
          const fallbackResponse = await fetch(`${API_URL}/public-chatbot`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              message: userMessage,
              conversation_history: historyToSend
            }),
          });
          
          if (!fallbackResponse.ok) {
            throw new Error(`Public API responded with status: ${fallbackResponse.status}`);
          }
          
          response = await fallbackResponse.json();
        }
      } else {
        // For non-logged-in users, use the public endpoint
        try {
          const apiResponse = await fetch(`${API_URL}/public-chatbot`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              message: userMessage,
              conversation_history: historyToSend
            }),
          });
          
          if (!apiResponse.ok) {
            throw new Error(`API responded with status: ${apiResponse.status}`);
          }
          
          response = await apiResponse.json();
        } catch (fetchError) {
          console.error('Network error fetching from public chatbot API:', fetchError);
          
          // Check if it's a first-time user (short conversation history)
          if (messages.length <= 2) {
            // Provide a helpful introduction instead of an error message
            const welcomeResponse = "Welcome to Pump Gym! I'm your fitness assistant. I can help you with information about our features such as personalized workout plans, diet tracking, and real-time exercise monitoring. What would you like to know about our app?";
            startTypewriter(welcomeResponse);
            setIsError(false);
            setIsTyping(false);
            return;
          }
          
          throw fetchError; // Re-throw to be caught by the outer catch
        }
      }
      
      if (!response.success) {
        throw new Error('Failed to get response from chatbot API');
      }
      
      // Start the typewriter animation with the bot's response
      startTypewriter(response.response);
      
      // If server returned a conversation history, use it to update our state (but keep typewriter animation)
      if (response.conversation_history && Array.isArray(response.conversation_history)) {
        // Keep the welcome message and add the server's conversation history, but maintain current animation
        const welcomeMessage = messages[0];
        const updatedHistory = [welcomeMessage, ...response.conversation_history.slice(0, -1)];
        setMessages([...updatedHistory, { text: '', sender: 'bot', isAnimating: true }]);
      }
      
      setIsError(false);
    } catch (error) {
      console.error('Error getting chatbot response:', error);
      setIsError(true);
      
      // Provide a helpful message when there's an error instead of a random offline response
      const firstTimeUser = messages.length <= 2;
      let errorResponse;
      
      if (firstTimeUser) {
        errorResponse = "Welcome to Pump Gym! I can provide information about our workout plans, diet tracking, and exercise monitoring features. What would you like to know about?";
      } else if (isLoggedIn) {
        errorResponse = "I'm having trouble connecting to our AI system right now. This might be due to an authentication issue. Please try again in a moment, or log out and back in if the problem persists.";
      } else {
        // For existing conversations, use the fallback responses
        const offlineResponses = fallbackResponses.offline;
        errorResponse = offlineResponses[Math.floor(Math.random() * offlineResponses.length)];
      }
      
      startTypewriter(errorResponse);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() === '') return;
    
    // Add user message
    setMessages(prev => [...prev, { text: input, sender: 'user' }]);
    
    // Process and respond
    processMessage(input);
    
    // Clear input
    setInput('');
  };

  return (
    <div className={`fixed font-spaceGrotesk bottom-20 right-5 z-50 w-80 sm:w-96 bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 transform ${isOpen ? 'scale-100' : 'scale-0'}`}>
      {/* Header */}
      <div className="bg-primary text-white p-4 flex justify-between items-center">
        <h3 className="font-bold font-play">Pump Gym Assistant</h3>
        <div className="flex items-center space-x-2">
          <button 
            onClick={clearChatHistory} 
            className="text-white hover:text-gray-200 tooltip"
            title="Clear chat history"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Chat Messages */}
      <div className="p-4 h-80 overflow-y-auto bg-gray-50">
        {messages.map((msg, index) => (
          <div 
            key={index} 
            className={`mb-3 ${msg.sender === 'user' ? 'text-right' : 'text-left'}`}
          >
            <div 
              className={`inline-block p-3 rounded-lg ${
                msg.sender === 'user' 
                  ? 'bg-primary text-white rounded-br-none' 
                  : 'bg-gray-200 text-gray-800 rounded-bl-none'
              }`}
            >
              {/* Preserve line breaks and handle markdown for bot messages */}
              {msg.sender === 'bot' ? (
                <>
                  {msg.isAnimating && index === messages.length - 1 ? (
                    <div className="chat-typewriter" style={{ whiteSpace: 'pre-line', position: 'relative' }}>
                      <span dangerouslySetInnerHTML={{ __html: formatMarkdown(typewriterText) || '' }} />
                      <span className="typing-cursor">|</span>
                    </div>
                  ) : (
                    <div 
                      className="features-content"
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.text) }}
                    />
                  )}
                </>
              ) : (
                msg.text
              )}
            </div>
          </div>
        ))}
        {isTyping && !isTypewriting && (
          <div className="text-left mb-3">
            <div className="inline-block p-2 bg-gray-200 text-gray-800 rounded-lg rounded-bl-none">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-75"></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-150"></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input Form */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 p-4 flex">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onClick={() => {}}
          placeholder="Type your message..."
          className="flex-grow border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button 
          type="submit"
          className="ml-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/50 transition-colors"
          disabled={isTyping || isTypewriting}
        >
          {isTyping || isTypewriting ? (
            <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      </form>
      
      {/* Add CSS for improved cursor animation */}
      <style jsx="true">{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .typing-cursor {
          display: inline-block;
          width: 2px;
          height: 1em;
          background-color: transparent;
          color: #333;
          margin-left: 1px;
          font-weight: bold;
          animation: blink 1s infinite;
          position: relative;
          top: -1px;
        }
        .features-content {
          min-width: 220px;
        }
      `}</style>
    </div>
  );
};

export default ChatBot;
